import {drawLine, drawRectangle, getBoundsForRectAroundCentre, mapMotorToCanvas} from "../utils/map_utils";
import {getPrefixedUrl} from "../../../utils/utils";
import BaseLayer from "./BaseLayer";
import {Grid, Stack} from "@mui/material";
import React, {Suspense} from "react";

const Plot = React.lazy(() => import("react-plotly.js"));

function isValidValue(val) {
    return (typeof(val) !== "undefined" && val !== null)
}

function createRankObject(inputObject, metric) {
    // Convert the input object to an array of objects
    let inputArray = Object.values(inputObject)

    if (metric === 'f_error') {
        inputArray = inputArray.filter(elem => isValidValue(elem["F-Error(%)"]));
        inputArray.sort((a, b) => a["F-Error(%)"] - b["F-Error(%)"]);
    } else {
        inputArray = inputArray.filter(elem => isValidValue(elem["Diff Z 1(um)"]) && isValidValue(elem["Diff Z 2(um)"]));
        inputArray.sort((a, b) => Math.min(a["Diff Z 1(um)"], a["Diff Z 2(um)"]) - Math.min(b["Diff Z 1(um)"], b["Diff Z 2(um)"]));
    }
    
    // Sort the array based on the "F-Error(%)" property in ascending order
  
    // Create the rank object
    const rankObject = {};
    inputArray.forEach((item, index) => {
      rankObject[item.Field] = index + 1; // Adding 1 to start ranking from 1
    });
  
    return [rankObject, inputArray.length];
  }

function valueToHeatColor(value, min, max) {
    // Define the gradient colors from green to red
    const gradientColors = [
        '#00FF00',  // Green (low values)
        '#FFFF00',  // Yellow
        '#FF0000'   // Red (high values)
    ];

    // Normalize the value between 0 and 1
    const normalizedValue = (value - min) / (max - min);
    
    // Calculate the index in the gradient array
    const index = normalizedValue * (gradientColors.length - 1);
    
    // Interpolate between the two colors
    return interpolateColor(gradientColors[Math.floor(index)], gradientColors[Math.ceil(index)], index % 1);
}

function interpolateColor(color1, color2, t) {
    const hex1 = parseInt(color1.slice(1), 16);
    const hex2 = parseInt(color2.slice(1), 16);
    const r1 = (hex1 >> 16) & 255;
    const g1 = (hex1 >> 8) & 255;
    const b1 = hex1 & 255;
    const r2 = (hex2 >> 16) & 255;
    const g2 = (hex2 >> 8) & 255;
    const b2 = hex2 & 255;

    const r = Math.round(r1 + t * (r2 - r1));
    const g = Math.round(g1 + t * (g2 - g1));
    const b = Math.round(b1 + t * (b2 - b1));

    return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
}


class FocusTest extends BaseLayer {
    
    id = "focustest" + this.props.metric;
    label = "Focus Test " + this.props.metric;
    defaultVisible = this.props.tileViewerState[this.id];
    
    onFeatureSelect = () => undefined;
    
    draw = () => new Promise((resolve, reject) => {
        let {focusTestMap, pathUnitMap, deviceSettings, tileFlag, textColor} = this.props;
        let [fieldRankMap, maxMetricRank] = focusTestMap["fields"] ? createRankObject(focusTestMap["fields"], this.props.metric) : [{}, 1]
        let minMetricRank = 1
        let {y_fields, tile_width, tile_height, uperpixel} = this.props.slide;

        Object.values(pathUnitMap.scanCellMap).forEach(scanCell => {
            const {xPosition, yPosition} = scanCell.position;

            let {x, y} = mapMotorToCanvas(xPosition, yPosition, this.props.slide,
                {device_settings: deviceSettings});
            
            if (tileFlag['reverseYTileOrder']['flagY'])
                y = y_fields * tile_height * uperpixel - y;
            
            let x_motor = x;
            let y_motor = y;
            x = Math.floor(x / (tile_width * uperpixel));
            y = Math.floor(y / (tile_height * uperpixel));
            
            if (scanCell.selectedForScan) {
                let focusRes = focusTestMap["fields"][`${scanCell.key}.jpg`]            
                let metricErrorForField = null

                if (!focusRes || focusRes['discarded']) {
                    metricErrorForField = null
                } else if (this.props.metric === 'f_error') {
                    metricErrorForField = focusTestMap["fields"][`${scanCell.key}.jpg`]["F-Error(%)"]
                } else {
                    metricErrorForField = Math.min(focusTestMap["fields"][`${scanCell.key}.jpg`]["Diff Z 1(um)"],
                        focusTestMap["fields"][`${scanCell.key}.jpg`]["Diff Z 2(um)"])

                }
                let color = (fieldRankMap[`${scanCell.key}.jpg`]) ? valueToHeatColor(fieldRankMap[`${scanCell.key}.jpg`], minMetricRank, maxMetricRank) : '#000000'
                let bounds = (metricErrorForField != null) ? getBoundsForRectAroundCentre(tile_width, tile_height, uperpixel, x, y, 0.3) :
                    getBoundsForRectAroundCentre(tile_width, tile_height, uperpixel, x, y, 0.1);
                let border = (metricErrorForField != null) ? 6 : 2;

                let fieldsToDisplay = []
                let imgLinkPrefix = getPrefixedUrl(`fda_metrics/focusing_test/${scanCell.key}.jpg/`, this.props.slide)
                let link1 = `${imgLinkPrefix}/matched_crop_cur_TOP.jpg`;
                let link2 = `${imgLinkPrefix}/matched_crop_other_TOP.jpg`;
                let debugFolderUrl = "app://" + this.props.slide.loc_on_machine + "/scans/" + this.props.slide.bucket_name + "/" + this.props.slide.tile_viewer_path + "/"
                    + `fda_metrics/focusing_test/${scanCell.key}.jpg/`;
                let resStr = "";

                if (this.props.metric == 'f_error') {
                    resStr = `ΔF(%): ${metricErrorForField}`
                } else if (this.props.metric == 'z_diff') {
                    resStr = `ΔZ(um): ${metricErrorForField}`
                }
                if (focusRes) {
                    for (const key in focusRes) {
                        console.log(typeof focusRes[key])
                        if (typeof focusRes[key] === 'string' || typeof focusRes[key] === 'number' || typeof focusRes[key] === 'boolean') {
                          fieldsToDisplay.push(<Grid key={key}>{key}: {focusRes[key]}</Grid>)
                        }
                    }
                }

                let popupText = (
                    <Stack>
                        <Grid>
                            {fieldsToDisplay}
                            <a href={link1} target={"_blank"}>{"[Original Img]"}</a>
                            <a href={link2} target={"_blank"}>{" [Best Focus Img]"}</a>
                            <a href={debugFolderUrl} target={"_blank"}>{" [Debug]"}</a>
                        </Grid>
                    </Stack>
                );

                if(focusRes && !focusRes['discarded']) {
                    let plotDataZStack = {
                        x: focusRes['plot']['zstack']['X'],
                        y: focusRes['plot']['zstack']['Y'],
                        type: "scatter",
                        mode: "markers",
                        marker: {color: "blue"},
                        name: 'Z Stack'
                    }
    
                    let plotDataOriginal = {
                        x: focusRes['plot']['original']['X'],
                        y: focusRes['plot']['original']['Y'],
                        type: "scatter",
                        mode: "markers",
                        marker: {color: "red"},
                        name: 'Original'
                    }
                    let popupPlot = (
                        <Grid>
                            <Suspense fallback={<div>Loading...</div>}>
                                <Plot data={[plotDataZStack, plotDataOriginal]}/>
                            </Suspense>
                        </Grid>
                    );
                    
                    drawLine(x_motor + 0.1 * tile_height * uperpixel, y_motor, 1, this.layer.getSource(),
                        resStr, 10, "#f4c2c2", popupPlot, textColor, 10);
                }
                

                drawRectangle(bounds, this.layer.getSource(), false, border, color, popupText, textColor);
            }
        });
        resolve();
    })
}

export default FocusTest;
